<script lang="ts">
  import { onMount } from 'svelte';
  import SoldOutOverlay from './SoldOutOverlay.svelte';
  import SoldOutConfetti from './SoldOutConfetti.svelte';
  import SoldOutMatrix from './SoldOutMatrix.svelte';

  let activeAnimation: 'none' | 'overlay' | 'confetti' | 'matrix' = 'none';
  let showFloatingParticles = false;

  const triggerAnimation = (type: 'overlay' | 'confetti' | 'matrix') => {
    activeAnimation = 'none'; // Reset first
    showFloatingParticles = false; // Reset particles
    setTimeout(() => {
      activeAnimation = type;
    }, 100);
  };

  const closeAnimation = () => {
    activeAnimation = 'none';
    showFloatingParticles = false;
  };

  const handleShowParticles = (show: boolean) => {
    showFloatingParticles = show;
  };
</script>

<div class="demo-container">
  <div class="content">
    <h1>Sold Out Animation Demo</h1>
    <p>Choose an animation style to preview:</p>
    
    <div class="button-grid">
      <button 
        class="demo-button overlay-btn" 
        on:click={() => triggerAnimation('overlay')}
      >
        🎯 Option 1: Dramatic Overlay
        <small>Cross-strike effect with pulse animation</small>
      </button>
      
      <button 
        class="demo-button confetti-btn" 
        on:click={() => triggerAnimation('confetti')}
      >
        🎉 Option 2: Confetti Explosion
        <small>Typewriter effect with confetti bursts</small>
      </button>
      
      <button 
        class="demo-button matrix-btn" 
        on:click={() => triggerAnimation('matrix')}
      >
        💻 Option 3: Matrix Digital Rain
        <small>Cyberpunk style with neon effects</small>
      </button>
    </div>

    {#if activeAnimation !== 'none'}
      <button class="close-btn" on:click={closeAnimation}>
        ✕ Close Animation
      </button>
    {/if}

    <div class="event-preview">
      <h2>Vidamor Chapter 6</h2>
      <p>Experience the sold out moment...</p>
      <div class="mock-ticket-count">
        <span>Tickets Available: 0 / 100</span>
      </div>
    </div>
  </div>
</div>

<!-- Animation Components - Confetti last to ensure proper layering -->
<SoldOutOverlay isVisible={activeAnimation === 'overlay'} />
<SoldOutMatrix isVisible={activeAnimation === 'matrix'} />
<SoldOutConfetti isVisible={activeAnimation === 'confetti'} onShowParticles={handleShowParticles} />

<!-- TOP LEVEL PARTICLES - GUARANTEED TO BE ON TOP -->
{#if showFloatingParticles || activeAnimation === 'confetti'}
  <div class="top-level-particles">
    {#each Array(12) as _, i}
      <div class="particle particle-{i + 1}">🎫</div>
    {/each}
  </div>
{/if}

<style>
  .demo-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #1e1e1e, #2d2d2d);
    color: white;
    padding: 2rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  .content {
    max-width: 800px;
    margin: 0 auto;
    text-align: center;
  }

  h1 {
    font-size: 3rem;
    margin-bottom: 1rem;
    background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .button-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2rem;
    margin: 3rem 0;
  }

  .demo-button {
    padding: 2rem;
    border: none;
    border-radius: 12px;
    font-size: 1.2rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    min-height: 120px;
    justify-content: center;
  }

  .demo-button small {
    font-size: 0.9rem;
    font-weight: 400;
    opacity: 0.8;
  }

  .overlay-btn {
    background: linear-gradient(135deg, #ff4444, #cc3333);
    color: white;
  }

  .overlay-btn:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(255, 68, 68, 0.4);
  }

  .confetti-btn {
    background: linear-gradient(135deg, #4ecdc4, #44a08d);
    color: white;
  }

  .confetti-btn:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(78, 205, 196, 0.4);
  }

  .matrix-btn {
    background: linear-gradient(135deg, #00ff00, #009900);
    color: black;
  }

  .matrix-btn:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 255, 0, 0.4);
  }

  .close-btn {
    position: fixed;
    top: 2rem;
    right: 2rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    color: white;
    font-size: 1.2rem;
    cursor: pointer;
    z-index: 10002;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
  }

  .close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  .event-preview {
    margin-top: 3rem;
    padding: 2rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.05);
  }

  .event-preview h2 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  .mock-ticket-count {
    margin-top: 1rem;
    padding: 1rem;
    background: rgba(255, 68, 68, 0.2);
    border-radius: 8px;
    font-weight: 600;
  }

  /* TOP LEVEL PARTICLES - ABOVE EVERYTHING */
  .top-level-particles {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 25vh;
    pointer-events: none;
    z-index: 999999; /* HIGHEST POSSIBLE Z-INDEX */
  }

  .particle {
    position: absolute;
    font-size: 4rem; /* HUGE SIZE */
    opacity: 1;
    animation: float 3s ease-in-out infinite;
    color: red; /* BRIGHT RED */
    text-shadow: 0 0 20px red;
    background: yellow; /* YELLOW BACKGROUND */
    border: 3px solid blue; /* BLUE BORDER */
  }

  .particle-1 { top: 10%; left: 10%; animation-delay: 0s; }
  .particle-2 { top: 15%; right: 15%; animation-delay: 0.5s; }
  .particle-3 { top: 25%; left: 20%; animation-delay: 1s; }
  .particle-4 { top: 35%; right: 10%; animation-delay: 1.5s; }
  .particle-5 { bottom: 30%; left: 15%; animation-delay: 2s; }
  .particle-6 { bottom: 25%; right: 20%; animation-delay: 2.5s; }
  .particle-7 { bottom: 15%; left: 25%; animation-delay: 0.3s; }
  .particle-8 { bottom: 10%; right: 25%; animation-delay: 0.8s; }
  .particle-9 { top: 45%; left: 5%; animation-delay: 1.3s; }
  .particle-10 { top: 50%; right: 5%; animation-delay: 1.8s; }
  .particle-11 { top: 60%; left: 10%; animation-delay: 2.3s; }
  .particle-12 { top: 70%; right: 15%; animation-delay: 2.8s; }

  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    25% { transform: translateY(-20px) rotate(90deg); }
    50% { transform: translateY(-10px) rotate(180deg); }
    75% { transform: translateY(-30px) rotate(270deg); }
  }

  @media (max-width: 768px) {
    .demo-container {
      padding: 1rem;
    }
    
    h1 {
      font-size: 2rem;
    }
    
    .button-grid {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
    
    .demo-button {
      padding: 1.5rem;
      min-height: 100px;
    }

    .particle {
      font-size: 1.5rem;
    }
  }
</style> 